import { Box } from "@mui/material"
import { useFocusable } from "@noriginmedia/norigin-spatial-navigation"
import React, { useMemo } from "react"
import Switch from "react-switch"

import { DGText } from "../../../components/common/DGText"
import useTranslate from "../../../hooks/use-translate"
import { s } from "../../../utilities/common"
import { theme, TI } from "../../../utilities/theme"

import { PText } from "./common"

interface SwitchProps {
  focusKey?: string
  onChange: (e: boolean) => void
  title: string
  checked: boolean
  disabled?: boolean
  description?: string
  scrollFn?: (y: number) => void
  focusParagraph?: boolean
}
export const SwitchBar = ({
  focusKey,
  onChange,
  title,
  checked,
  disabled = false,
  description,
  scrollFn,
  focusParagraph = true,
}: SwitchProps) => {
  const t = useTranslate()
  const { ref, focused } = useFocusable({
    focusKey,
    onEnterPress: () => {
      if (!disabled) {
        onChange(!checked)
        setTimeout(() => {
          ref.current?.blur()
          setTimeout(() => {
            ref.current?.focus()
          }, 200)
        }, 10)
      }
    },
    onFocus: ({ top: y }, __, query) => {
      if (!(query && query.noScroll)) scrollFn?.(y)
    },
  })

  const ariaLabel = useMemo(
    () =>
      `${title}, ${
        disabled && checked
          ? t("tracking__settings_always_active")
          : checked
            ? t("settings_on")
            : t("settings_off")
      }`,
    [checked, checked, disabled],
  )

  return (
    <Box>
      <Box
        ref={ref}
        aria-label={ariaLabel}
        tabIndex={0}
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          borderTop: "1px solid",
          borderBottom: "1px solid",
          borderColor: focused ? "transparent" : theme.palette.white[20],
          p: s("40px"),
          borderRadius: focused ? s("16px") : undefined,
          transform: focused ? "scale(1.14)" : undefined,
          backgroundColor: focused
            ? theme.palette.secondary.variant
            : undefined,
          boxShadow: focused
            ? `${s("0px 30px 112px 0px")} rgba(0, 0, 0, 0.6)`
            : undefined,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <DGText sx={{ color: theme.palette.white.main }} tx={TI.Regular04tv}>
            {title}
          </DGText>
          <PText>
            {disabled && checked
              ? t("tracking__settings_always_active")
              : checked
                ? t("settings_on")
                : t("settings_off")}
          </PText>
        </Box>
        <Switch
          disabled={disabled}
          checked={checked}
          onChange={onChange}
          handleDiameter={s(40)}
          checkedIcon={false}
          uncheckedIcon={false}
          onColor={"#525965"}
          offColor={"#525965"}
          height={s(28)}
          width={s(56)}
        />
      </Box>
      {description && (
        <Description
          scrollFn={scrollFn}
          text={description}
          focusable={focusParagraph}
        />
      )}
    </Box>
  )
}

const Description = ({
  text,
  scrollFn,
  focusable = true,
}: {
  text: string
  scrollFn?: (y: number) => void
  focusable?: boolean
}) => {
  const { ref } = useFocusable({
    focusable,
    onFocus: ({ top: y }, __, query) => {
      if (!(query && query.noScroll)) scrollFn?.(y)
    },
  })

  return (
    <Box
      aria-label={text}
      tabIndex={0}
      sx={{
        p: s("40px"),
      }}
      ref={ref}
    >
      <PText>{text}</PText>
    </Box>
  )
}
